(() => {
  'use strict';
  if (window.WellHomeCarousel) return;

  /*
    Modulo generico de carrusel de la home. Mismo comportamiento que el de la ficha
    de producto (`initRelatedProgress` en wellhome-product-quality.js): la barra es
    indicador Y control, y no hay avance automatico.

    Con teclado no hace falta control propio: el carril lleva `tabindex="0"` y el
    navegador ya lo desplaza con las flechas.

    FLECHAS (anadidas el 2026-08-25 para el enlazado de categorias hijas). Son
    OPCIONALES: quien no ponga `[data-carousel-prev]` / `[data-carousel-next]` se
    comporta igual que antes, asi que las tres secciones que ya usaban el modulo
    -Top ventas, relacionados y opiniones- no cambian. Se anaden aqui y no en una
    copia local porque el diseno de la categoria las pide y ya habia un carrusel
    propio en `collection-wellhome-main.liquid`: dos carruseles distintos en la
    misma tienda se separan solos con el tiempo.

    Y SE OCULTAN SI NO HAY DESBORDE, igual que la barra. Es la leccion de las
    resenas del 24-08: un control sobre algo que no se desplaza miente. Con una
    sola categoria hija -pasa en tres categorias reales: muebles-de-despacho,
    muebles-de-comedor y muebles-recibidor- no hay nada que desplazar.
  */
  const setup = (carousel) => {
    if (!carousel || carousel.dataset.carouselReady === 'true') return;
    const track = carousel.querySelector('[data-carousel-track]');
    const progress = carousel.querySelector('[data-carousel-progress]');
    if (!track) return;
    carousel.dataset.carouselReady = 'true';
    const controller = new AbortController();
    carousel.whCarouselController = controller;
    const { signal } = controller;
    let frame = null;
    let thumbRatio = 1;

    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    // El pulgar es el <span>; el carril que se puede pulsar es su contenedor. Se toma
    // por `parentElement` y no por clase para que el modulo siga siendo generico.
    const rail = progress?.parentElement;

    const update = () => {
      frame = null;
      const travel = Math.max(0, track.scrollWidth - track.clientWidth);
      // 1px de tolerancia porque `scrollLeft` es fraccionario con zoom del navegador
      // y sin holgura la flecha derecha se queda activa al final del recorrido.
      const hayDesborde = travel > 1;

      /*
        LA BARRA SE OCULTA SI NO HAY DESBORDE, igual que las flechas (2026-09-16).
        Hasta hoy el calculo de abajo topaba el pulgar en el 100 % y pintaba el carril
        LLENO de lado a lado, o sea un control que no controla nada y un indicador que
        dice "estas viendo el 100 %" con cuatro tarjetas que caben de sobra. Es la misma
        leccion de las resenas del 24-08 que ya se aplico a las flechas, solo que aqui
        quedo a medias: `wh-carousel--estatico` se calculaba DENTRO del `if (prev || next)`,
        asi que en los tres carruseles sin flechas -Top ventas de la portada, la misma
        banda en la 404, relacionados y opiniones de la ficha- no llegaba a ponerse nunca.

        Se hace con `hidden` y no solo con la clase porque el CSS del carril vive en
        `snippets/wh-product-carousel.liquid` y las secciones que lo reutilizan no lo
        redeclaran: una regla `.wh-carousel--estatico .wh-carousel-progress` solo taparia
        las paginas donde ese fichero se renderiza. El atributo va en el DOM y no depende
        de que hoja llegue. La clase se sigue poniendo, que es de lo que cuelga
        `.wh-carousel--estatico .cw-kids` en la pagina de categoria.

        `aria-hidden` ya estaba en el marcado, asi que para un lector de pantalla no
        cambia nada: esto solo quita del medio lo que se ve.
      */
      carousel.classList.toggle('wh-carousel--estatico', !hayDesborde);
      if (rail) rail.hidden = !hayDesborde;
      if (prev) {
        prev.hidden = !hayDesborde;
        prev.disabled = track.scrollLeft <= 1;
      }
      if (next) {
        next.hidden = !hayDesborde;
        next.disabled = track.scrollLeft >= travel - 1;
      }

      if (!progress) return;
      const thumb = track.scrollWidth ? Math.max(16, Math.min(100, (track.clientWidth / track.scrollWidth) * 100)) : 100;
      thumbRatio = thumb / 100;
      const available = 100 - thumb;
      const position = travel ? (track.scrollLeft / travel) * available : 0;
      progress.style.width = `${thumb}%`;
      // El translate va en % del ANCHO DEL PULGAR, no del carril, asi que hay que
      // reescalar: mover un 10 % del carril con un pulgar del 50 % son 20 % de si mismo.
      progress.style.transform = `translateX(${(position / Math.max(thumb, 1)) * 100}%)`;
    };

    // Un paso es una VISTA COMPLETA menos una tarjeta, para que no se pierda el hilo
    // visual entre paginas. Si no se puede medir una tarjeta se cae al 80 % del carril,
    // que es lo que hacia el carrusel local que este modulo sustituye.
    const paso = () => {
      const tarjeta = track.firstElementChild;
      if (!tarjeta) return track.clientWidth * 0.8;
      const ancho = tarjeta.getBoundingClientRect().width;
      if (!ancho) return track.clientWidth * 0.8;
      const porVista = Math.max(1, Math.round(track.clientWidth / ancho));
      return ancho * Math.max(1, porVista - 1);
    };

    const mover = (signo) => {
      const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: signo * paso(), behavior: suave ? 'smooth' : 'auto' });
    };
    prev?.addEventListener('click', () => mover(-1), { signal });
    next?.addEventListener('click', () => mover(1), { signal });
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    track.addEventListener('scroll', requestUpdate, { passive: true, signal });
    window.addEventListener('resize', requestUpdate, { passive: true, signal });
    // El `resize` de ventana no basta para las flechas: el carril tambien cambia de ancho
    // cuando se abre el panel de filtros o cuando cargan las fuentes, y ahi el desborde
    // aparece o desaparece sin que la ventana se haya movido.
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(requestUpdate);
      ro.observe(track);
      signal.addEventListener('abort', () => ro.disconnect());
    }

    if (rail) {
      // La posicion se mide respecto al CENTRO del pulgar: contra su borde, al soltar en
      // un extremo el carrusel se quedaria siempre corto por medio pulgar.
      const scrollToPointer = (event) => {
        const travel = Math.max(0, track.scrollWidth - track.clientWidth);
        if (!travel) return;
        const rect = rail.getBoundingClientRect();
        const thumbWidth = rect.width * thumbRatio;
        const usable = Math.max(1, rect.width - thumbWidth);
        const offset = event.clientX - rect.left - (thumbWidth / 2);
        const ratio = Math.min(1, Math.max(0, offset / usable));
        track.scrollLeft = ratio * travel;
      };

      // `is-dragging` va en los DOS: en el carril cambia el cursor, y en la pista apaga
      // el imantado y la animacion suave, que peleaban con cada `scrollLeft` y hacian
      // que el carrusel avanzase a tirones en vez de seguir al raton. Al soltar vuelven,
      // y el navegador cuadra solo con la tarjeta mas cercana.
      rail.addEventListener('pointerdown', (event) => {
        if (track.scrollWidth - track.clientWidth <= 0) return;
        rail.setPointerCapture(event.pointerId);
        rail.classList.add('is-dragging');
        track.classList.add('is-dragging');
        scrollToPointer(event);
        event.preventDefault();
      }, { signal });

      rail.addEventListener('pointermove', (event) => {
        if (!rail.hasPointerCapture(event.pointerId)) return;
        scrollToPointer(event);
      }, { signal });

      const endDrag = (event) => {
        if (!rail.hasPointerCapture(event.pointerId)) return;
        rail.releasePointerCapture(event.pointerId);
        rail.classList.remove('is-dragging');
        track.classList.remove('is-dragging');
      };
      rail.addEventListener('pointerup', endDrag, { signal });
      rail.addEventListener('pointercancel', endDrag, { signal });
    }

    update();
  };

  const setupAll = (root = document) => root.querySelectorAll('[data-carousel]').forEach(setup);
  const cleanupAll = (root) => root.querySelectorAll('[data-carousel]').forEach((carousel) => {
    carousel.whCarouselController?.abort();
    carousel.dataset.carouselReady = 'false';
  });

  window.WellHomeCarousel = { setup, setupAll, cleanupAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupAll(), { once: true });
  else setupAll();
  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupAll(event.target));
})();
